import { useSelector } from "react-redux";
import {
  ContactAndTravelerSelectors,
  useTravelerSelection,
} from "@apac/checkout";
import { usePassengerCountString } from "@apac/hooks";
import { FlightSearchSelectors } from "@b2bportal/core-flights";
import { ProductType } from "@b2bportal/core-types";
import type { Traveler } from "@b2bportal/traveler-api";
import { useCheckoutStateSelector } from "@hopper-b2b/checkout";

export type SeatType = "OnLap" | "OwnSeat" | "NotTraveling";

export interface UseConnectedPassengerSelectionReturn {
  context: {
    isLoading: boolean;
    disabled: boolean;
    hasError: boolean;
    passengerList: Traveler[];
    allSelectedPassengerIds: string[];
    searchPassengerCounts: Record<string, number>;
    currentPassenger: Traveler;
    shouldOpenInfantSeatPicker: boolean;
    openPassengerFormModal: boolean;
    passengerSearchCountsString: string;
    displayNoSeatedInfantsAlert: boolean;
    isPassengerValid: boolean;
  };
  handlers: {
    handleSelectPassenger: (passengerId: string) => void;
    handleEditClick: (passengerId: string) => void;
    handleDeletePassenger: (passengerId: string) => void;
    handleClickAddTraveler: () => void;
    handleCloseForm: () => void;
    handleUpdatePassenger: (passenger: Traveler) => void;
    handleInfantPickerModalClose: () => void;
    handleInfantPickerModalContinue: (seatType: SeatType) => void;
  };
}

export const useConnectedPassengerSelection = () => {
  const selectedSeatedTravelerIds: string[] = useCheckoutStateSelector(
    ContactAndTravelerSelectors.getSelectedTravelerIds
  );
  const selectedLapInfantIds: string[] = useCheckoutStateSelector(
    ContactAndTravelerSelectors.getSelectedLapInfantIds
  );

  let searchPassengerCounts = useSelector(
    FlightSearchSelectors.passengerCountSelector
  );

  const passengerSearchCountsString = usePassengerCountString(
    { type: "conjunction" },
    searchPassengerCounts
  );

  const {
    context: {
      travelerList,
      currentTraveler,
      isTravelerValid,
      traveler,
      isLoading,
      openTravelerFormModal,
      disabled,
      travelerError,
      openInfantDialog,
    },
    handlers: {
      handleSelectTraveler,
      handleEditClick,
      handleDeleteTraveler,
      handleUpdateTraveler,
      handleClickAddTraveler,
      handleCloseForm,
      setTraveler,
      setTravelerValid,
      handleInfantModalClose,
      handleInfantModalContinue,
    },
  } = useTravelerSelection(ProductType.FLIGHTS, false);

  return {
    context: {
      isLoading,
      disabled,
      hasError: travelerError,
      passengerList: travelerList,
      allSelectedPassengerIds: [
        ...selectedSeatedTravelerIds,
        ...selectedLapInfantIds,
      ],
      searchPassengerCounts,
      currentPassenger: currentTraveler,
      shouldOpenInfantSeatPicker: openInfantDialog,
      openPassengerFormModal: openTravelerFormModal,
      passengerSearchCountsString,
      passenger: traveler,
      isPassengerValid: isTravelerValid,
    },
    handlers: {
      handleSelectPassenger: handleSelectTraveler,
      handleEditClick,
      handleDeletePassenger: handleDeleteTraveler,
      handleClickAddTraveler,
      handleCloseForm,
      handleUpdatePassenger: handleUpdateTraveler,
      handleInfantPickerModalClose: handleInfantModalClose,
      handleInfantPickerModalContinue: handleInfantModalContinue,
      setPassenger: setTraveler,
      setPassengerValid: setTravelerValid,
    },
  };
};
